/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { GetUtProjectsSearchParametersSchema } from '../../../../common/src/domain/drone/utProject/utProjectPaths';
import { useTranslate } from '../../mixins/translate/translate';
import { IStoreState } from '../../store/IStoreState';
import { openApiFetchDelete, openApiFetchPost } from '../../store/openApiFetch';
import { useOpenApiFetch } from '../../store/useOpenApiFetch';
import { Roles } from '../../utils/enums/Roles';
import { IconButton } from '../common/iconButtons/IconButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelSection,
    PanelBody,
    EditPanelInput,
    PanelWithoutForm,
    TableSubText,
    TableEmphasizedText,
    RightTray,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeadRow,
    TableHeader,
    TableRow,
    TableButtons,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    TopBarDownloadButton,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import { hasStringValue } from './existanceCheck';

type SearchParameters = GetUtProjectsSearchParametersSchema;

function textToMaxChars(text: string | undefined | null, maxChars: number) {
    if (!hasStringValue(text)) {
        return '';
    }
    if (text.length < maxChars) {
        return text;
    }
    return `${text.substring(0, maxChars - 3)}...`;
}

export function ImportProjectList() {
    const translate = useTranslate();
    const [searchParameters, setSearchParameters] = useState<SearchParameters>({});
    const [searchForm, setSearchForm] = useState<{ name: string }>({ name: '' });
    const navigate = useNavigate();
    const languageCode = useSelector((state: IStoreState) => state.language.code);
    const userRole = useSelector((state: IStoreState) => state.authentication.role);

    const { data, reload, isLoading } = useOpenApiFetch('/api/utprojects', 'get', { searchParameters }, []);

    function search() {
        setSearchParameters((i) => ({ ...i, name: searchForm.name }));
    }

    async function deleteItem(projectId: string) {
        await openApiFetchDelete('/api/utprojects/{projectId}', {
            projectId,
        });
        await reload();
    }

    async function copyItem(projectId: string) {
        await openApiFetchPost(
            '/api/utprojects/{projectId}/copy',
            {
                projectId,
            },
            undefined,
        );
        await reload();
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title="UT Projects" icon="fa-bar-chart-o" />
                </TopbarLeft>
                <TopbarRight>
                    {userRole === Roles.Administrator && (
                        <TopBarDownloadButton
                            path="/api/utprojects/download/excel"
                            query={{
                                languageCode,
                            }}
                            text={translate('common.excelExport')}
                            disabled={data?.items.length === 0}
                        />
                    )}
                </TopbarRight>
            </Topbar>
            <Content>
                <Tray>
                    <PanelWithoutForm>
                        <PanelBody noPadding={true}>
                            <Table hover={true}>
                                <TableHead>
                                    <TableHeadRow light={true}>
                                        <TableHeader>{translate('common.name')}</TableHeader>
                                        <TableHeader>{translate('common.client')}</TableHeader>
                                        <TableHeader>{translate('common.date')}</TableHeader>
                                        <TableHeader>{translate('licence.deviceName')}</TableHeader>
                                        <TableHeader>{translate('project.conductedBy')}</TableHeader>
                                        <TableHeader>{translate('common.survey')}</TableHeader>
                                        <TableHeader>&nbsp; </TableHeader>
                                    </TableHeadRow>
                                </TableHead>
                                {isLoading || !data ? (
                                    <TableBody>
                                        <TableRow>
                                            <TableData colSpan={5}>
                                                <Spinner />
                                            </TableData>
                                        </TableRow>
                                    </TableBody>
                                ) : (
                                    <TableBody>
                                        {data.items.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableData>
                                                    <TableEmphasizedText text={item.name} />
                                                    <TableSubText text={textToMaxChars(item.description, 50)} />
                                                </TableData>
                                                <TableData nowrap={true}>{item.clientName}</TableData>
                                                <TableData nowrap={true}>
                                                    <NullableFormattedDate value={item.createdOnDateTime} />
                                                </TableData>
                                                <TableData>{item.deviceName}</TableData>
                                                <TableData>
                                                    {item.pilotName}
                                                    {item.pilotName && item.surveyorName && ', '}
                                                    {item.surveyorName}
                                                </TableData>
                                                <TableData>
                                                    {item.objects.name}
                                                    <Link to={`/surveys/${item.surveyId}`}>
                                                        <div className="tooltipZXY">
                                                            {textToMaxChars(item.surveys.description, 50)}{' '}
                                                            {item.surveys.surveyedOn && (
                                                                <div>
                                                                    (<NullableFormattedDate value={item.surveys.surveyedOn} />)
                                                                </div>
                                                            )}
                                                            <span className="tooltiptext">{item.surveys.description}</span>
                                                        </div>
                                                    </Link>
                                                </TableData>
                                                <TableButtons large>
                                                    {item.surveyId === null && (
                                                        <IconButton
                                                            iconClass="fa-sign-in"
                                                            onClick={() => navigate(`/projects/${item.id}/import`)}
                                                            text="Import"
                                                        />
                                                    )}
                                                    <IconButton iconClass="fa-copy" onClick={async () => copyItem(item.id)} />
                                                    {item.surveyId === null && (
                                                        <IconRemoveButton
                                                            removeQuestion={translate('common.confirmDelete', item.name)}
                                                            onClick={async () => deleteItem(item.id)}
                                                        />
                                                    )}
                                                </TableButtons>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </PanelBody>
                    </PanelWithoutForm>
                </Tray>

                <RightTray>
                    <div className="admin-form">
                        <h4> {translate('common.searchItem', translate('project.projects'))}</h4>
                        <EditPanelSection topBorder={true}>
                            <EditPanelInput
                                name="name"
                                placeholder={translate('common.name')}
                                value={searchForm.name}
                                icon="fa fa-building"
                                handleChange={(value) => setSearchForm((i) => ({ ...i, name: value }))}
                                handleEnter={search}
                                handleBlur={search}
                                disabled={isLoading}
                            />
                        </EditPanelSection>
                        <EditPanelButtons topBorder>
                            <EditPanelButton onClick={() => setSearchParameters({})} text={translate('common.reset')} />
                        </EditPanelButtons>
                    </div>
                </RightTray>
            </Content>
        </>
    );
}
